/**
 * Multi-select — Finder-semantics selection helpers + keyboard wiring.
 *
 * Sits on top of the `linked-views.js` shared-selection state so the card
 * list and the PDF overlay layer read from one `Set<structId>`.
 *
 * Finder semantics:
 *   click            → single-select (clears prior, sets anchor)
 *   shift+click      → range from anchor to here (replaces prior)
 *   cmd/ctrl+click   → toggle individual (anchor unchanged)
 *   esc              → clear selection
 *   cmd/ctrl+a       → select all visible
 *
 * Range ordering uses the card-list document order (not bbox geometry).
 *
 * The anchor is the *last single-clicked* element — Cmd-click does NOT
 * move it. Shift-click can re-extend from the same anchor, which mirrors
 * Finder's behaviour.
 *
 * Pure module: no implicit DOM lookups beyond what's in opts.
 *
 * @module multi-select
 */
/**
 * Return the selection mode implied by a click MouseEvent.
 *
 * `shift` wins over `cmd` when both are held — same as Finder.
 */
export function modeFromEvent(ev) {
    if (!ev)
        return "single";
    if (ev.shiftKey)
        return "range";
    if (ev.metaKey || ev.ctrlKey)
        return "toggle";
    return "single";
}
/**
 * Compute a range slice (inclusive) from `orderedIds` between two endpoints.
 * Either endpoint may come first in the array — we sort by index.
 *
 * Anchor missing or not in the list → fall back to first element.
 * Target missing → returns [].
 */
export function computeRange(orderedIds, anchorId, targetId) {
    if (!Array.isArray(orderedIds) || orderedIds.length === 0)
        return [];
    const ti = orderedIds.indexOf(targetId);
    if (ti < 0)
        return [];
    let ai = anchorId ? orderedIds.indexOf(anchorId) : 0;
    if (ai < 0)
        ai = 0;
    const lo = Math.min(ai, ti);
    const hi = Math.max(ai, ti);
    return orderedIds.slice(lo, hi + 1);
}
/**
 * Apply a click-mode selection update to a linkedViews handle.
 */
export function applyClickSelection({ linkedViews, mode, id, orderedIds }) {
    if (!linkedViews || !id)
        return;
    if (mode === "range") {
        const anchor = linkedViews.getAnchor ? linkedViews.getAnchor() : null;
        const ids = computeRange(orderedIds || [], anchor, id);
        if (ids.length === 0) {
            linkedViews.selectStructId(id, "single");
            return;
        }
        if (typeof linkedViews.setSelection === "function") {
            linkedViews.setSelection(new Set(ids), { keepAnchor: true });
        }
        else {
            // Fallback to per-id toggle wiring.
            linkedViews.clearSelection();
            for (const x of ids)
                linkedViews.selectStructId(x, "toggle");
        }
        return;
    }
    if (mode === "toggle") {
        linkedViews.selectStructId(id, "toggle");
        return;
    }
    linkedViews.selectStructId(id, "single");
}
/**
 * Wire the global selection keyboard handler.
 *
 * Returns a `detach()` function the caller is expected to invoke before
 * remounting (so we don't double-fire on re-mount).
 */
export function wireSelectionKeyboard(opts) {
    const { document: doc, getLinkedViews, getOrderedIds, getSelected, onDeleteSelection, onUndo, onRedo, } = opts;
    function inField(t) {
        if (!t)
            return false;
        const el = t;
        const tag = el.tagName ? String(el.tagName).toLowerCase() : "";
        if (tag === "input" || tag === "textarea" || tag === "select")
            return true;
        if (el.getAttribute && el.getAttribute("contenteditable") === "true")
            return true;
        if (el.isContentEditable === true)
            return true;
        return false;
    }
    function handler(ev) {
        const target = ev.target;
        const lv = getLinkedViews && getLinkedViews();
        const isField = inField(target);
        if (ev.key === "Escape") {
            if (lv && !isField && getSelected && getSelected().size > 0) {
                lv.clearSelection();
                // Don't preventDefault — other Esc handlers (modal close,
                // popover close) still need to fire on the same event.
            }
            return;
        }
        if ((ev.key === "Backspace" || ev.key === "Delete") && !isField) {
            if (getSelected && getSelected().size > 0) {
                ev.preventDefault();
                if (typeof onDeleteSelection === "function")
                    onDeleteSelection();
            }
            return;
        }
        if ((ev.metaKey || ev.ctrlKey) && (ev.key === "a" || ev.key === "A") && !isField) {
            const ids = getOrderedIds ? getOrderedIds() : [];
            if (ids.length > 0 && lv && typeof lv.setSelection === "function") {
                ev.preventDefault();
                lv.setSelection(new Set(ids), { keepAnchor: false });
            }
            return;
        }
        // Undo / redo: only fire ours when not in a text field. The legacy
        // editor.js global Ctrl-Z routes to `els.undoBtn` (which only enables
        // when the legacy article is dirty); for the card-pane flow the
        // local op stack is the source of truth.
        if ((ev.metaKey || ev.ctrlKey) && !isField) {
            const k = String(ev.key).toLowerCase();
            if (k === "z" && !ev.shiftKey) {
                if (typeof onUndo === "function") {
                    ev.preventDefault();
                    onUndo();
                }
                return;
            }
            if ((k === "z" && ev.shiftKey) || k === "y") {
                if (typeof onRedo === "function") {
                    ev.preventDefault();
                    onRedo();
                }
                return;
            }
        }
    }
    doc.addEventListener("keydown", handler, true);
    return function detach() {
        doc.removeEventListener("keydown", handler, true);
    };
}
